import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ICON_REGISTRY } from '../generated/icon-registry';
import type { ColorToken } from '../color-token';
import type { IconName } from '../generated/icon-names';

export type IconSize = 'tiny' | 'smallest' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'inherit';

const SIZE_MAP: Record<IconSize, string> = {
	tiny: '0.5rem',
	smallest: '0.75rem',
	small: '1rem',
	medium: '1.25rem',
	large: '1.5rem',
	xlarge: '2rem',
	xxlarge: '2.5rem',
	inherit: '1em',
};

let instanceCounter = 0;

/**
 * Universeel icon-component dat SVG inline embedt in de DOM.
 *
 * Voordelen vs. icon font / background-image / mask-image:
 * - Werkt in html2canvas-pro (PNG-export) en PrinceXML (PDF-export)
 * - Kleur instelbaar via CSS `color` of `color` input (design tokens)
 * - Compile-time typesafe via IconName union type
 *
 * @example
 * <app-icon name="chevron-down" />
 * <app-icon name="export" color="primary-1" size="large" />
 * <app-icon name="pijl-links-beneden" [rotate]="180" />
 */
@Component({
	selector: 'app-icon',
	standalone: true,
	templateUrl: './icon.component.html',
	styleUrls: ['./icon.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[style.--icon-color]': 'resolvedColor()',
		'[style.--icon-hover-color]': 'resolvedHoverColor()',
		'[class.has-hover-color]': 'hasHoverColor()',
		'[style.width]': 'resolvedSize()',
		'[style.height]': 'resolvedSize()',
		'[style.transform]': 'resolvedTransform()',
		'[attr.aria-hidden]': '"true"',
		'[attr.name]': 'name()',
	},
})
export class IconComponent {
	private readonly sanitizer = inject(DomSanitizer);
	/** Uniek ID per instantie — voorkomt SVG ID-botsingen bij meerdere instanties (bijv. Error.svg) */
	private readonly instanceId = ++instanceCounter;

	/** Naam van het icon — compile-time typesafe via IconName */
	name = input.required<IconName>();

	/**
	 * Kleur van het icon. Accepteert:
	 * - Design token naam: 'primary-1', 'error-1', etc. → `var(--color-cl-primary-1)`
	 * - undefined → erft CSS `color` van parent (standaard)
	 */
	color = input<ColorToken | undefined>(undefined);

	/**
	 * Grootte van het icon.
	 * Voorgedefinieerde waarden: 'tiny' (0.5rem) | 'smallest' (0.75rem) | 'small' (1rem) | 'medium' (1.25rem)
	 *                             | 'large' (1.5rem) | 'xlarge' (2rem) | 'xxlarge' (2.5rem) | 'inherit' (1em)
	 */
	size = input<IconSize>('medium');

	/**
	 * Rotatiehoek in graden. Handig voor gespiegelde varianten:
	 * bijv. arrow-up = <app-icon name="arrow-down" [rotate]="180">
	 */
	rotate = input<0 | 90 | 180 | 270>(0);

	/**
	 * Hover-kleur van het icon.
	 * Zelfde formaat als `color`; als gezet krijgt het icon ook pointer-cursor op hover.
	 * De cursor kan onderdrukt worden via de CSS custom property `--icon-cursor` op een ancestor:
	 * @example button:disabled { --icon-cursor: default }
	 */
	hoverColor = input<string | undefined>(undefined);

	protected readonly resolvedColor = computed(() => {
		const c = this.color();
		if (!c) return undefined;
		return `var(--color-cl-${c})`;
	});

	protected readonly resolvedSize = computed(() => {
		return SIZE_MAP[this.size()];
	});

	protected readonly resolvedTransform = computed(() => {
		const r = this.rotate();
		return r ? `rotate(${r}deg)` : null;
	});

	protected readonly resolvedHoverColor = computed(() => {
		const c = this.hoverColor();
		if (!c) return undefined;
		return /^(chart-[a-z]+|[a-z]+-\d+)$/.test(c) ? `var(--color-cl-${c})` : c;
	});

	protected readonly hasHoverColor = computed(() => !!this.hoverColor());

	protected readonly svgContent = computed((): SafeHtml => {
		const raw = ICON_REGISTRY[this.name()];
		if (!raw) {
			console.warn(`[app-icon] Icon "${this.name()}" not found in registry`);
			return this.sanitizer.bypassSecurityTrustHtml('');
		}
		// Maak SVG-interne IDs uniek per instantie om botsingen te voorkomen
		// (bijv. Error.svg gebruikt <clipPath id="error__a">)
		if (!raw.includes(' id="')) return this.sanitizer.bypassSecurityTrustHtml(raw);
		const id = this.instanceId;
		const uniquified = raw.replaceAll(/\bid="([^"]+)"/g, `id="icon-${id}-$1"`).replaceAll(/url\(#([^)]+)\)/g, `url(#icon-${id}-$1)`);
		return this.sanitizer.bypassSecurityTrustHtml(uniquified);
	});
}
